import React, {useEffect, useState} from 'react';
import styled from 'styled-components';
import MotifFormField from '@ey/motif-react-wrapper/FormField';
import MotifLabel from '@ey/motif-react-wrapper/Label';
import MotifInput from '@ey/motif-react-wrapper/Input';
import MotifSelect from '@ey/motif-react-wrapper/Select';
import MotifOption from '@ey/motif-react-wrapper/Option';
import MotifErrorMessage from '@ey/motif-react-wrapper/ErrorMessage';
import MotifIcon from '@ey/motif-react-wrapper/Icon';
import LoadingIndicator from '@ey/canvascoreservices/LoadingIndicator';
import {IconoirCustomLabelSolid, IconoirWarningCircle} from '@ey-xd/motif-icon';
import Ellipse from '@ey/canvascoreservices/Ellipse';
import {stringFormat} from '@ey/utils';
import Modal from '../../ADCModal/Modal';
import {tagFillColor} from '../../../../../../../../util/uiconstants';
import Utility from '../../../../../../../../util/utilityfunctions';

export default function Content(props) {
	const {
		isEditModal,
		entityId,
		showModal,
		setShowModal,
		labels,
		isLoading,
		setIsLoading,
		onEntityCreated,
		createTagAction,
		errorComponent,
		tagGroups,
		tagTypeId,
		tagItem,
		editTagAction
	} = props;

	const [formData, setFormData] = useState({
		tagName: '',
		tagGroupName: '',
		tagColor: ''
	});
	const [error, setError] = useState({
		tagName: false,
		tagGroupName: false,
		tagColor: false
	});

	const modalDescription = isEditModal ? labels.editModalDescription : labels.createModalDescription;

	useEffect(() => {
		if (isEditModal && tagItem) {
			setFormData({
				tagName: tagItem?.engagementTagName,
				tagGroupName: tagItem?.engagementTagGroupName,
				tagColor: tagItem?.colorCode.toLowerCase()
			});
		}
	}, [tagItem]);

	const handleChange = (val, inputName) => {
		setFormData({
			...formData,
			[inputName]: val
		});
	};

	const onCloseModal = () => {
		setShowModal(false);
	};

	const saveAndClose = () => {
		if (!validateInput()) return;

		setIsLoading(true);
		const action = isEditModal ? updateTag : createData;

		action()
			.then(() => {
				setIsLoading(false);
				onCloseModal();
			})
			.catch(() => {
				setIsLoading(false);
			});
	};

	const validateInput = () => {
		let isValid = true;
		const newError = {...error};
		Object.keys(formData).forEach((key) => {
			if (formData[key].length === 0) {
				isValid = false;
				newError[key] = true;
			} else {
				newError[key] = false;
			}
		});
		setError(newError);
		return isValid;
	};

	const saveAndCreateAnother = () => {
		if (validateInput()) {
			setIsLoading(true);
			createData()
				.then(() => {
					setFormData({
						tagName: '',
						tagGroupName: '',
						tagColor: ''
					});
					setIsLoading(false);
				})
				.catch(() => {
					setIsLoading(false);
				});
		}
	};

	const noResultTemplate = () => <MotifOption disabled>{labels.noresultsLabel}</MotifOption>;

	const createData = () => {
		const payload = {
			name: formData.tagName,
			engagementTagGroupId: tagGroups.find((tagGroup) => tagGroup.value === formData.tagGroupName).key,
			colorCode: formData.tagColor,
			entityId: entityId,
			tagGroupName: formData.tagGroupName
		};
		if (tagTypeId) {
			payload.tagTypeId = tagTypeId;
		}
		return createTagAction(payload);
	};

	const updateTag = () => {
		const payload = {
			name: formData.tagName,
			engagementTagGroupId: tagGroups.find((tagGroup) => tagGroup.value === formData.tagGroupName).key,
			colorCode: formData.tagColor,
			entityId: entityId,
			tagGroupName: formData.tagGroupName
		};
		if (tagTypeId) {
			payload.tagTypeId = tagTypeId;
		}
		return editTagAction(payload);
	};

	const getVisibleOption = () => {
		return window.devicePixelRatio > 1 ? 4 : 5;
	};

	return (
		<Modal
			show={showModal}
			title={isEditModal ? labels.editModalTitle : labels.createModalTitle}
			confirmBtnTitle={isEditModal ? labels.save : labels.saveAndCloseLabel}
			saveCreateBtnTitle={isEditModal ? null : labels.saveAndCreateLabel}
			closeBtnTitle={labels.cancelLabel}
			onHide={() => onCloseModal()}
			isOkButtonDisabled={isLoading}
			isSaveCreateButtonDisabled={isLoading}
			isCancelButtonDisabled={isLoading}
			modalContainerClass={'entities-modal'}
			modalsize={'small'}
			modalonmodal
			showButtons={true}
			onOkClick={() => {
				saveAndClose();
			}}
			onSaveCreateClick={() => {
				saveAndCreateAnother();
			}}
			showSaveAndCreateNewButton={true}
			okTitle={labels.saveAndCloseLabel}
			disableClose={isLoading}
			showError
			errorChildren={errorComponent}
		>
			<StyledFormSection>
				{isLoading && (
					<section className={'loaderSection'}>
						<LoadingIndicator show view={'inlineView'} />
					</section>
				)}
				{!isLoading && (
					<>
						<section className="modal-description motif-body1-default-light">
							{Utility.createRichTextElement(
								isEditModal
									? stringFormat(modalDescription, labels.save)
									: stringFormat(modalDescription, labels.saveAndCloseLabel, labels.saveAndCreateLabel)
							)}
						</section>
						<section className="separator" />
						<section className="tagName">
							<MotifFormField role="group">
								<MotifLabel position="in">{labels.tagNameLabel}</MotifLabel>
								<MotifInput
									value={formData.tagName}
									labelPosition="in"
									hideClearButton={true}
									onChange={(ev) => handleChange(ev.target.value, 'tagName')}
									maxLength={5}
									suffix={error.tagName && <MotifIcon fill="var(--red-600)" size="16" src={IconoirWarningCircle} />}
								/>
								<section className="error-message">
									{error.tagName && <MotifErrorMessage role="alert">{labels.required}</MotifErrorMessage>}
								</section>
							</MotifFormField>
						</section>
						<section className="tagGroup">
							<MotifFormField role="group">
								<MotifLabel position="in">{labels.tagGroupNameLabel}</MotifLabel>
								<MotifSelect
									value={formData.tagGroupName}
									onChange={(ev) => handleChange(ev, 'tagGroupName')}
									filter={true}
									multiple={false}
									visibleOptions={getVisibleOption()}
									labelPosition="in"
									noResultsTemplate={noResultTemplate}
								>
									{tagGroups.map((tagGroup) => {
										return (
											<MotifOption key={tagGroup.key} value={tagGroup.value} label={tagGroup.value}>
												<Ellipse content={tagGroup.value} tooltip={tagGroup.value} width={650} noOfLines={1} />
											</MotifOption>
										);
									})}
								</MotifSelect>
								<section className="error-message">
									{error.tagGroupName && <MotifErrorMessage role="alert">{labels.required}</MotifErrorMessage>}
								</section>
							</MotifFormField>
						</section>
						<section className="tagColor">
							<MotifFormField role="group">
								<MotifLabel position="in">{labels.tagColorLabel}</MotifLabel>
								<MotifSelect
									labelPosition="in"
									value={formData.tagColor}
									options={labels.tagColors}
									visibleOptions={getVisibleOption()}
									onChange={(ev) => handleChange(ev, 'tagColor')}
								>
									{labels.tagColors.map((tagColor) => (
										<MotifOption key={tagColor.text} value={tagColor.color} label={tagColor.text}>
											<span className="tagLabel">
												<MotifIcon
													className={'tagLabelIcon'}
													src={IconoirCustomLabelSolid}
													fill={tagFillColor[tagColor.color.toLowerCase()]}
													size={20}
												/>{' '}
												{tagColor.text}
											</span>
										</MotifOption>
									))}
								</MotifSelect>
								<section className="error-message">
									{error.tagColor && <MotifErrorMessage role="alert">{labels.required}</MotifErrorMessage>}
								</section>
							</MotifFormField>
						</section>
					</>
				)}
			</StyledFormSection>
		</Modal>
	);
}

const StyledFormSection = styled.section`
	min-height: var(--px-280);
	display: flex;
	flex-direction: column;
	.separator {
		border: 1px solid var(--neutrals-100);
		margin: var(--px-24) 0;
	}
	.error-message {
		min-height: var(--px-24);
		p {
			margin-top: 0;
		}
	}
	.tagName {
		input {
			height: var(--px-44);
		}
	}
	.tagGroup {
		.motif-select-clean-value {
			display: none;
		}
	}
	.tagLabel {
		display: flex;
		align-items: center;
		.tagLabelIcon {
			display: flex;
		}
	}
`;
